import { useEffect, useState } from 'react';
import { toBool } from 'vopay';
import { useLocation } from 'react-router-dom';
import { ContactDetailView, ContactDetailData } from 'shared/contacts';
import { useClientData } from '@/providers/ClientData.provider';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import useAccountingSoftwareHostProps from '@/components/Software/useAccountingSoftwareHostProps';
import useActivityTypeVisibility from '@/hooks/useActivityTypeVisibility';

type ContactDetailProps = {
  contactID: string;
  detail: ContactDetailData;
  // Whether the new flow's embedded Receivables/Payables tabs may show (still
  // gated on the account's accounting locked feature). Off on the menu-linked
  // page, on for the orphan /accounting copy (ID-13335).
  accountingEnabled: boolean;
};

// The contact detail body — shared/contacts' ContactDetailView mounted with
// the portal's providers mapped onto its host props. The host owns the data
// via useContactDetailData (the page chrome reads the same instance, so edits
// inside the view refresh it too) and renders any chrome itself.
export default function ContactDetail({ contactID, detail, accountingEnabled }: ContactDetailProps) {
  const location = useLocation();
  const [tab, setTab] = useState('details');
  const accounting = useAccountingSoftwareHostProps();
  const { AccountingSoftwareIntegrationEnabled } = useLockedFeatures();
  const activityTypes = useActivityTypeVisibility();
  const { response } = useClientData();
  const { permissions } = usePermissions();

  const {
    userSettings: { timezone },
  } = useUserSettingsProvider();

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const newTab = search.get('cardTab');
    newTab && setTab(newTab);
  }, [location.search]);

  return (
    <ContactDetailView
      clientAccountId={contactID}
      clientAccountsEnabled={false}
      accounting={accounting}
      contactDetail={detail}
      tab={tab}
      onTabChange={setTab}
      accountingEnabled={accountingEnabled && Boolean(AccountingSoftwareIntegrationEnabled)}
      activityTypes={activityTypes}
      activityOptions={{
        persistFiltersInUrl: true,
        timezone,
        showIncludeSubAccounts: toBool(response.SubAccountPermissions) && permissions.ViewSubaccounts,
      }}
    />
  );
}
